import React, { useEffect, useMemo, useState } from "react";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  RefreshCw,
  Trash2,
  Repeat,
  X,
  CalendarDays,
  Clock3,
  MapPin,
  User,
  FileText,
  Send,
  LayoutGrid,
  Car,
  Truck,
  Plus,
  AlertTriangle,
} from "lucide-react";
import type { CreateShiftForm, ShiftRow, DriverRow, VehicleRow, ShiftTypeRow, RepeatMode, TemplateRow } from "./types";
import { calcWorkedHours, DAY_LABELS, BREAK_MINUTE_OPTIONS } from "./utils";
import { format, addMonths } from "date-fns";

type RepeatEndMode = "date" | "6months" | "12months";

interface ShiftFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formData: CreateShiftForm;
  setFormData: React.Dispatch<React.SetStateAction<CreateShiftForm>>;
  editingShift: ShiftRow | null;
  drivers: DriverRow[];
  vehicles: VehicleRow[];
  shiftTypes: ShiftTypeRow[];
  saving: boolean;
  onSave: () => void;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
  templates?: TemplateRow[];
  onApplyTemplate?: (template: TemplateRow) => void;
}

const ShiftFormDialog: React.FC<ShiftFormDialogProps> = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  editingShift,
  drivers,
  vehicles,
  shiftTypes,
  saving,
  onSave,
  onDelete,
  onCancel,
  templates = [],
  onApplyTemplate,
}) => {
  const [dialogTab, setDialogTab] = useState<"custom" | "templates" | "timeoff">("custom");
  const [repeatEndMode, setRepeatEndMode] = useState<RepeatEndMode>("date");
  const isRepeat = formData.repeat_mode !== "none";

  const toggleCustomDay = (day: number) => {
    setFormData((f) => {
      const days = f.repeat_custom_days.includes(day)
        ? f.repeat_custom_days.filter((d) => d !== day)
        : [...f.repeat_custom_days, day].sort();
      return { ...f, repeat_custom_days: days };
    });
  };

  const workedHours = calcWorkedHours(formData.start_time, formData.end_time, formData.break_minutes);

  const displayDateFull = useMemo(() => {
    try {
      return format(new Date(formData.date + "T00:00:00"), "d MMM yyyy");
    } catch {
      return formData.date;
    }
  }, [formData.date]);

  const selectedDriver = drivers.find((d) => d.driver_name === formData.driver_name);
  const driverLabel = selectedDriver
    ? selectedDriver.driver_firstname && selectedDriver.driver_lastname
      ? `${selectedDriver.driver_firstname} ${selectedDriver.driver_lastname}`
      : selectedDriver.driver_name
    : null;

  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  const selectedVehicle = vehicles.find((v) => v.vehicle_number === formData.vehicle_number);
  const vehicleLabel = selectedVehicle
    ? `${selectedVehicle.vehicle_number} — ${selectedVehicle.vehicle_type || "Unknown"} ${
        selectedVehicle.transmission_type === "Manual"
          ? "(Manual)"
          : selectedVehicle.transmission_type === "Automatic"
          ? "(Auto)"
          : ""
      }`
    : null;

  const selectedShiftType = shiftTypes.find((st) => st.code === formData.shift_type_code);

  const formatTime12 = (hhmm: string): string => {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${h12}:00 ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const timeOffTypes = shiftTypes.filter(
    (st) =>
      ["LEAVE", "SICK", "TRAINING", "STANDBY", "TIME_OFF"].includes(st.code?.toUpperCase()) ||
      !st.counts_as_worked_hours
  );

  const transmissionWarning = useMemo(() => {
    if (!selectedDriver || !selectedVehicle) return null;
    if (selectedVehicle.transmission_type === "Manual" && selectedDriver.can_drive_manual === false) {
      return `${driverLabel || selectedDriver.driver_name} can only drive Automatic vehicles, but ${selectedVehicle.vehicle_number} is Manual.`;
    }
    return null;
  }, [selectedDriver, selectedVehicle, driverLabel]);

  const handleRepeatEndModeChange = (mode: RepeatEndMode) => {
    setRepeatEndMode(mode);
    if (mode === "6months") {
      const endDate = format(addMonths(new Date(formData.date + "T00:00:00"), 6), "yyyy-MM-dd");
      setFormData((f) => ({ ...f, repeat_end_date: endDate }));
    } else if (mode === "12months") {
      const endDate = format(addMonths(new Date(formData.date + "T00:00:00"), 12), "yyyy-MM-dd");
      setFormData((f) => ({ ...f, repeat_end_date: endDate }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl w-[95vw] p-0 bg-gray-900 border-gray-700 text-white max-h-[92vh] overflow-hidden flex flex-col">
        <DialogTitle className="sr-only">{editingShift ? "Edit shift" : "New shift"}</DialogTitle>

        <div className="px-5 pt-4 pb-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <LayoutGrid className="h-5 w-5 text-blue-400" />
              </div>
              <h2 className="text-lg font-bold text-blue-400">
                {editingShift ? "Edit shift" : "New shift"}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              {!editingShift && (
                <button
                  onClick={() =>
                    setFormData((f) => ({ ...f, repeat_mode: f.repeat_mode === "none" ? "weekly" : "none" }))
                  }
                  className={`p-1.5 rounded-md transition-colors ${
                    isRepeat ? "text-blue-400 bg-blue-500/20" : "text-gray-500 hover:text-gray-300"
                  }`}
                  title="Toggle repeat"
                >
                  <Repeat className="h-4 w-4" />
                </button>
              )}
              {editingShift && editingShift.status !== "cancelled" && (
                <button
                  onClick={() => {
                    onCancel(editingShift.id);
                    onOpenChange(false);
                  }}
                  className="p-1.5 rounded-md text-gray-500 hover:text-yellow-400 transition-colors"
                  title="Cancel shift"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {editingShift && (
                <button
                  onClick={() => {
                    onDelete(editingShift.id);
                    onOpenChange(false);
                  }}
                  className="p-1.5 rounded-md text-gray-500 hover:text-red-400 transition-colors"
                  title="Delete shift"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-0 mt-4 border-b border-gray-700">
            {[
              { key: "custom" as const, label: "CUSTOM" },
              { key: "templates" as const, label: "TEMPLATES" },
              { key: "timeoff" as const, label: "TIME OFF" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setDialogTab(tab.key)}
                className={`px-4 py-2.5 text-xs font-semibold tracking-wider transition-colors relative ${
                  dialogTab === tab.key ? "text-blue-400" : "text-gray-500 hover:text-gray-300"
                }`}
              >
                {tab.label}
                {dialogTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t" />}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {dialogTab === "custom" && (
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <CalendarDays className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</span>
                </div>
                <div className="flex-1">
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData((f) => ({ ...f, date: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white h-10"
                  />
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <Clock3 className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Time</span>
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-[1fr_1fr_120px] gap-2">
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Start</label>
                      <Input
                        type="time"
                        value={formData.start_time}
                        onChange={(e) => setFormData((f) => ({ ...f, start_time: e.target.value }))}
                        className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">End</label>
                      <Input
                        type="time"
                        value={formData.end_time}
                        onChange={(e) => setFormData((f) => ({ ...f, end_time: e.target.value }))}
                        className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Break</label>
                      <Select
                        value={String(formData.break_minutes)}
                        onValueChange={(v) => setFormData((f) => ({ ...f, break_minutes: Number(v) }))}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-600 h-9 text-sm">
                          <SelectValue placeholder="Break" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          {BREAK_MINUTE_OPTIONS.map((mins) => (
                            <SelectItem key={mins} value={String(mins)}>
                              {mins === 0 ? "No break" : `${mins} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    {formatTime12(formData.start_time)} – {formatTime12(formData.end_time)} · {workedHours}h worked
                  </p>
                </div>
              </div>

              {!editingShift && (
                <>
                  <div className="border-t border-gray-800 my-1" />

                  <div className="flex items-start gap-4">
                    <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                      <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                        <Repeat className="h-4 w-4 text-blue-400" />
                      </div>
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Repeat</span>
                    </div>
                    <div className="flex-1">
                      <Select
                        value={formData.repeat_mode}
                        onValueChange={(v) => setFormData((f) => ({ ...f, repeat_mode: v as RepeatMode }))}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-600 h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600">
                          <SelectItem value="none">No repeat (single shift)</SelectItem>
                          <SelectItem value="daily">Every day</SelectItem>
                          <SelectItem value="weekly">Every week</SelectItem>
                          <SelectItem value="fortnightly">Every 2 weeks</SelectItem>
                          <SelectItem value="custom">Custom days</SelectItem>
                          <SelectItem value="cycle">Rotating pattern (e.g. 2 on / 2 off)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {(formData.repeat_mode === "custom" || formData.repeat_mode === "weekly") && (
                    <div className="flex items-start gap-4">
                      <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                          <Plus className="h-4 w-4 text-blue-400" />
                        </div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">Repeat on</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1.5">
                          {DAY_LABELS.map((label, idx) => {
                            const isActive =
                              formData.repeat_mode === "weekly"
                                ? (() => {
                                    const d = new Date(formData.date + "T00:00:00");
                                    const jsDay = d.getDay();
                                    const ourDay = jsDay === 0 ? 6 : jsDay - 1;
                                    return idx === ourDay;
                                  })()
                                : formData.repeat_custom_days.includes(idx);
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  if (formData.repeat_mode === "custom") toggleCustomDay(idx);
                                }}
                                disabled={formData.repeat_mode === "weekly"}
                                className={`w-10 h-9 rounded-md text-xs font-semibold transition-all ${
                                  isActive
                                    ? "bg-blue-500 text-white shadow-md shadow-blue-500/30"
                                    : "bg-gray-800 text-gray-500 hover:bg-gray-700 hover:text-gray-300"
                                } ${formData.repeat_mode === "weekly" ? "cursor-default" : "cursor-pointer"}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.repeat_mode === "cycle" && (
                    <div className="flex items-start gap-4">
                      <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                          <Plus className="h-4 w-4 text-blue-400" />
                        </div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">Rotation</span>
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Days on</label>
                            <Input
                              type="number"
                              min={1}
                              value={formData.repeat_cycle_days_on}
                              onChange={(e) =>
                                setFormData((f) => ({
                                  ...f,
                                  repeat_cycle_days_on: Math.max(1, Number(e.target.value) || 1),
                                }))
                              }
                              className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Days off</label>
                            <Input
                              type="number"
                              min={1}
                              value={formData.repeat_cycle_days_off}
                              onChange={(e) =>
                                setFormData((f) => ({
                                  ...f,
                                  repeat_cycle_days_off: Math.max(1, Number(e.target.value) || 1),
                                }))
                              }
                              className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                            />
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          {formData.repeat_cycle_days_on} day{formData.repeat_cycle_days_on === 1 ? "" : "s"} on, then{" "}
                          {formData.repeat_cycle_days_off} day{formData.repeat_cycle_days_off === 1 ? "" : "s"} off, repeating
                          continuously from {displayDateFull} -- a rolling cycle, not tied to calendar weeks, so it won't
                          drift like "every N weeks" can.
                        </p>
                      </div>
                    </div>
                  )}

                  {isRepeat && (
                    <div className="flex items-start gap-4">
                      <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                          <CalendarDays className="h-4 w-4 text-blue-400" />
                        </div>
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">Duration</span>
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {[
                            { key: "date" as RepeatEndMode, label: "Pick end date" },
                            { key: "6months" as RepeatEndMode, label: "6 months" },
                            { key: "12months" as RepeatEndMode, label: "12 months" },
                          ].map((opt) => (
                            <button
                              key={opt.key}
                              type="button"
                              onClick={() => handleRepeatEndModeChange(opt.key)}
                              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                                repeatEndMode === opt.key
                                  ? "bg-blue-500 text-white shadow-md shadow-blue-500/30"
                                  : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-300 border border-gray-700"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Starts</label>
                            <div className="bg-blue-500/20 border border-blue-500/30 rounded-md px-3 py-2 text-sm text-blue-300">
                              {displayDateFull}
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-gray-500 uppercase mb-0.5 block">Ends</label>
                            {repeatEndMode === "date" ? (
                              <Input
                                type="date"
                                value={formData.repeat_end_date}
                                onChange={(e) => setFormData((f) => ({ ...f, repeat_end_date: e.target.value }))}
                                className="bg-gray-800 border-gray-600 text-white h-9 text-sm"
                                min={formData.date}
                              />
                            ) : (
                              <div className="bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-gray-300">
                                {(() => {
                                  try {
                                    return format(new Date(formData.repeat_end_date + "T00:00:00"), "d MMM yyyy");
                                  } catch {
                                    return formData.repeat_end_date;
                                  }
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="border-t border-gray-800 my-1" />

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider leading-tight">Shift Type</span>
                </div>
                <div className="flex-1">
                  {selectedShiftType ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium"
                        style={{
                          backgroundColor: (selectedShiftType.default_colour_hex || "#3B82F6") + "25",
                          color: selectedShiftType.default_colour_hex || "#3B82F6",
                          border: `1px solid ${(selectedShiftType.default_colour_hex || "#3B82F6")}40`,
                        }}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: selectedShiftType.default_colour_hex || "#3B82F6" }}
                        />
                        {selectedShiftType.label}
                        <button
                          onClick={() => setFormData((f) => ({ ...f, shift_type_code: "" }))}
                          className="ml-1 hover:opacity-70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  ) : (
                    <Select
                      value={formData.shift_type_code}
                      onValueChange={(v) => setFormData((f) => ({ ...f, shift_type_code: v }))}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-600 h-10 text-gray-400">
                        <SelectValue placeholder="Select shift type..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600 max-h-72">
                        {shiftTypes.map((st) => (
                          <SelectItem key={st.code} value={st.code}>
                            <span className="flex items-center gap-2">
                              <span
                                className="inline-block w-3 h-3 rounded-full"
                                style={{ backgroundColor: st.default_colour_hex || "#3B82F6" }}
                              />
                              {st.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>

                    </Select>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <Car className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    Vehicle
                    {selectedShiftType?.vehicle_mandatory && <span className="text-red-400"> *</span>}
                  </span>
                </div>
                <div className="flex-1">
                  {vehicleLabel && formData.vehicle_number !== "__NONE__" ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                        {getIconStyle(selectedVehicle?.vehicle_type, vehicleTypes) === "truck" ? (
                          <Truck className="h-3.5 w-3.5" />
                        ) : (
                          <Car className="h-3.5 w-3.5" />
                        )}
                        {vehicleLabel}
                        <button
                          onClick={() => setFormData((f) => ({ ...f, vehicle_number: "__NONE__" }))}
                          className="ml-1 hover:opacity-70"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    </div>
                  ) : (
                    <Select
                      value={formData.vehicle_number}
                      onValueChange={(v) => setFormData((f) => ({ ...f, vehicle_number: v }))}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-600 h-10 text-gray-400">
                        <SelectValue placeholder="No vehicle" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                        <SelectItem value="__NONE__">No vehicle</SelectItem>
                        {vehicles.map((v) => (
                          <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                            <span className="flex items-center gap-2">
                              {getIconStyle(v.vehicle_type, vehicleTypes) === "truck" ? (
                                <Truck className="h-3.5 w-3.5 text-gray-400" />
                              ) : (
                                <Car className="h-3.5 w-3.5 text-gray-400" />
                              )}
                              {v.vehicle_number} — {v.vehicle_type || "Unknown"}
                              <span
                                className={`text-xs px-1.5 py-0.5 rounded ${
                                  v.transmission_type === "Manual"
                                    ? "bg-orange-500/20 text-orange-400"
                                    : "bg-green-500/20 text-green-400"
                                }`}
                              >
                                {v.transmission_type === "Manual" ? "Manual" : "Auto"}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {selectedShiftType?.vehicle_mandatory && (!formData.vehicle_number || formData.vehicle_number === "__NONE__") && (
                    <p className="mt-2 text-xs text-amber-400">
                      {selectedShiftType.label} shifts need a vehicle so the car shows as committed on the roster.
                    </p>
                  )}

                  {transmissionWarning && (
                    <div className="mt-2 flex items-start gap-2 p-2 rounded-md bg-red-900/30 border border-red-700/50">
                      <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-300">{transmissionWarning}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <User className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Employee</span>
                </div>
                <div className="flex-1">
                  {driverLabel ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          <User className="h-3.5 w-3.5" />
                          {driverLabel}
                          {selectedDriver?.can_drive_manual === false && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400 ml-1">
                              Auto only
                            </span>
                          )}
                          {selectedDriver?.can_drive_manual === true && (
                            <span className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-400 ml-1">
                              Auto & Manual
                            </span>
                          )}
                          <button
                            onClick={() => setFormData((f) => ({ ...f, driver_name: "" }))}
                            className="ml-1 hover:opacity-70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      </div>
                      <Select value="" onValueChange={(v) => setFormData((f) => ({ ...f, driver_name: v }))}>
                        <SelectTrigger className="bg-gray-800 border-gray-600 h-8 text-gray-500 text-xs">
                          <span className="text-gray-500">Change employee...</span>
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                          {drivers.map((d) => (
                            <SelectItem key={d.driver_name} value={d.driver_name}>
                              <span className="flex items-center gap-2">
                                {d.driver_firstname && d.driver_lastname
                                  ? `${d.driver_firstname} ${d.driver_lastname}`
                                  : d.driver_name}
                                {d.can_drive_manual === false && (
                                  <span className="text-[10px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400">
                                    Auto only
                                  </span>
                                )}
                                {d.can_drive_manual === true && (
                                  <span className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-400">
                                    Auto & Manual
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                    </div>
                  ) : (
                    <Select
                      value={formData.driver_name}
                      onValueChange={(v) => setFormData((f) => ({ ...f, driver_name: v }))}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-600 h-10 text-gray-400">
                        <SelectValue placeholder="Add employee(s)..." />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                        {drivers.map((d) => (
                          <SelectItem key={d.driver_name} value={d.driver_name}>
                            <span className="flex items-center gap-2">
                              {d.driver_firstname && d.driver_lastname
                                ? `${d.driver_firstname} ${d.driver_lastname}`
                                : d.driver_name}

                              {d.can_drive_manual === false && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-orange-500/20 text-orange-400">
                                  Auto only
                                </span>
                              )}
                              {d.can_drive_manual === true && (
                                <span className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-400">
                                  Auto & Manual
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-800 my-1" />

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notes</span>
                </div>
                <div className="flex-1 space-y-2">
                  <Textarea
                    value={formData.driver_visible_notes}
                    onChange={(e) => setFormData((f) => ({ ...f, driver_visible_notes: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white min-h-[60px] text-sm"
                    rows={2}
                    placeholder="Add a note (visible to driver)..."
                  />
                  <Textarea
                    value={formData.admin_notes}
                    onChange={(e) => setFormData((f) => ({ ...f, admin_notes: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white min-h-[50px] text-sm"
                    rows={2}
                    placeholder="Admin notes (internal only)..."
                  />
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-1">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <Send className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Publish</span>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <Switch
                    checked={formData.status === "published"}
                    onCheckedChange={(checked) =>
                      setFormData((f) => ({ ...f, status: checked ? "published" : "draft" }))
                    }
                  />
                  <span className={`text-sm ${formData.status === "published" ? "text-green-400" : "text-gray-500"}`}>
                    {formData.status === "published" ? "Published" : "Draft"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {dialogTab === "templates" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-400 mb-4">Quickly create a shift from a saved template.</p>
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <LayoutGrid className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No templates saved yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        if (onApplyTemplate) onApplyTemplate(t);
                        setDialogTab("custom");
                      }}
                      className="w-full text-left p-3 rounded-lg border border-gray-700 bg-gray-800/60 hover:border-blue-500/50 hover:bg-gray-800/80 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-white">{t.template_name}</p>
                        <Badge variant="outline" className="text-[10px] border-gray-600 text-gray-400">
                          {t.template_type === "week" ? "Week" : "Day"} · {t.shift_definitions.length} shifts
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Created {format(new Date(t.created_at), "d MMM yyyy")}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {dialogTab === "timeoff" && (
            <div className="space-y-5">
              <p className="text-sm text-gray-400 mb-2">Schedule time off, leave, or non-driving shifts.</p>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <User className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Employee</span>
                </div>
                <div className="flex-1">
                  <Select
                    value={formData.driver_name}
                    onValueChange={(v) => setFormData((f) => ({ ...f, driver_name: v }))}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-600 h-10 text-gray-400">
                      <SelectValue placeholder="Select employee..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                      {drivers.map((d) => (
                        <SelectItem key={d.driver_name} value={d.driver_name}>
                          {d.driver_firstname && d.driver_lastname
                            ? `${d.driver_firstname} ${d.driver_lastname}`
                            : d.driver_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <CalendarDays className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Type</span>
                </div>
                <div className="flex-1">
                  <Select
                    value={formData.shift_type_code}
                    onValueChange={(v) => setFormData((f) => ({ ...f, shift_type_code: v }))}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-600 h-10">
                      <SelectValue placeholder="Select leave type..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      {(timeOffTypes.length > 0 ? timeOffTypes : shiftTypes).map((st) => (
                        <SelectItem key={st.code} value={st.code}>
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-full"
                              style={{ backgroundColor: st.default_colour_hex || "#F59E0B" }}
                            />
                            {st.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <CalendarDays className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</span>
                </div>
                <div className="flex-1">
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData((f) => ({ ...f, date: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white h-10"
                  />
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex items-center gap-3 w-24 shrink-0 pt-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Notes</span>
                </div>
                <div className="flex-1">
                  <Textarea
                    value={formData.driver_visible_notes}
                    onChange={(e) => setFormData((f) => ({ ...f, driver_visible_notes: e.target.value }))}
                    className="bg-gray-800 border-gray-600 text-white min-h-[60px] text-sm"
                    rows={2}
                    placeholder="Reason for time off..."
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-700 flex items-center justify-end gap-3 bg-gray-900/80">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="px-6 border-gray-600 text-gray-300 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            className="px-6 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> Saving...
              </>
            ) : editingShift ? (
              "Save"
            ) : (
              "Review & Create"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftFormDialog;